import { PERMISSION } from '@/app/permissions';
import { holdsPermission } from '@/app/holdsPermission';
import { INSTITUTION_PERMISSIONS } from '@/features/admin/institutions/institutionTypes';
import { APPLICATIONS_READ_ANY } from '@/features/funding/applicationPermissions';
import type { NavItem } from '@/app/shells/cabinet/types/NavItem';

// `label` is a translation key into i18n/dictionaries/appShell.ts, not literal text — resolved
// at render time via useTranslation so the sidebar/document-title stay in sync with the
// selected language.
export const NAV: NavItem[] = [
  { to: '/app/cabinet', label: 'navOverview', icon: 'home-line' },
  { to: '/app/profil', label: 'navProfile', icon: 'user-account' },
  { to: '/app/cereri', label: 'navMyRequests', icon: 'document' },
  { to: '/app/publicatii', label: 'navPublications', icon: 'book' },
  { to: '/app/instruiri', label: 'navTrainings', icon: 'education' },
  // No permission gate: adding a program/session (POST .../programs, .../sessions) is already
  // scoped by org membership (OrgScopeEnforcer), not training.manage — MAIA_ADMIN's own screen
  // for every institution — so an institution's own staff needs this to reach either one at all.
  { to: '/app/formare-mea', label: 'navMyInstitution', icon: 'education' },
  // Filing is the advisors', the chambers' and CCAR's alone (use-case diagrams, caiet de
  // sarcini). Gated on the first of the two permissions POST .../applications demands, so the
  // tab does not offer MAIA_ADMIN, AIPA or a plain account a form the backend answers 403 to.
  {
    to: '/app/finantare/aplicare',
    label: 'navFunding',
    icon: 'receipt-check',
    permission: PERMISSION.applicationsCreateOwn,
  },
  // The other side of the same counter: every application filed on the platform, which is AIPA's
  // own screen („Accesează cererile", T165). It carries the same words as the entry above and never
  // stands beside it — filing asks for applications.create:own, which AIPA and MAIA_ADMIN are
  // refused, and reading the register asks for permissions neither an advisor nor a chamber holds.
  {
    to: '/app/finantare/cereri',
    label: 'navApplicationsRegister',
    icon: 'bullet-list',
    permission: APPLICATIONS_READ_ANY,
  },
  // The organiser's own events: proposing one and following it (tor.md §6.11). Gated on the same
  // permission EventController#create requires.
  {
    to: '/app/evenimentele-mele',
    label: 'navMyEvents',
    icon: 'calendar',
    permission: PERMISSION.eventsContribute,
  },
  // No permission gate: creating a private space (POST /api/v1/forums, privateSpace: true) is
  // open to any authenticated user, the same as the topics and replies inside one.
  { to: '/app/comunitate', label: 'navMyCommunity', icon: 'bullet-list' },
  {
    to: '/app/cercetare',
    label: 'navResearchOrg',
    icon: 'document',
    permission: PERMISSION.researchContribute,
  },
  // No role gate: CONSILIER_AGRICOL is granted by validation, not held beforehand (§6.3), so
  // gating this entry on it made registering impossible — the one screen where an account without
  // the role fills in the profile that earns it was the one the role was required to reach. The
  // route is RequireSession alone, and the page itself says what an unregistered account is
  // looking at.
  { to: '/app/consilier', label: 'navAdvisorProfile', icon: 'user-account' },
  {
    // Beside the advisor's own profile, because it is the same person's work: §6.3 puts the
    // advisor in the field and §6.7 asks what they saw there. Permission-gated rather than
    // role-gated so the chambers and CCAR, who also hold it, reach the screen too.
    to: '/app/adoptii',
    label: 'navAdoptions',
    icon: 'document',
    permission: PERMISSION.researchAdoptionReport,
  },
  {
    to: '/app/furnizor',
    label: 'navProviderServices',
    icon: 'services',
    roles: ['COMPANIE_INPUT', 'CCAR', 'CAMERA_AGRICOLA', 'FURNIZOR_CONSILIERE'],
  },
  {
    to: '/app/evenimente',
    label: 'navEvents',
    icon: 'calendar',
    permission: PERMISSION.eventsManage,
  },
  {
    to: '/app/formare',
    label: 'navTrainingAdmin',
    icon: 'education',
    permission: PERMISSION.trainingManage,
  },
];

export const ADMIN_NAV: NavItem[] = [
  {
    to: '/app/aprobare',
    label: 'adminNavApproval',
    icon: 'check-all',
    permission: PERMISSION.contentApprove,
  },
  {
    to: '/app/admin/consilieri',
    label: 'adminNavAdvisorValidation',
    icon: 'shield',
    permission: PERMISSION.advisorsValidate,
  },
  {
    to: '/app/admin/stiri',
    label: 'adminNavNews',
    icon: 'card-book',
    permission: PERMISSION.contentManage,
  },
  {
    to: '/app/admin/pagini',
    label: 'adminNavPages',
    icon: 'document',
    permission: PERMISSION.contentManage,
  },
  {
    to: '/app/admin/acasa',
    label: 'adminNavHome',
    icon: 'home-line',
    permission: PERMISSION.contentManage,
  },
  {
    to: '/app/admin/biblioteca',
    label: 'adminNavLibrary',
    icon: 'card-book',
    permission: PERMISSION.contentManage,
  },
  {
    to: '/app/admin/harta',
    label: 'adminNavMapPoints',
    // No map-pin in the generated sprite; dot-grid is a scatter of points, which is what this
    // screen administers.
    icon: 'dot-grid',
    permission: PERMISSION.geoAdmin,
  },
  {
    to: '/app/admin/contact',
    label: 'adminNavContact',
    icon: 'circle-info',
    permission: PERMISSION.contentManage,
  },
  // Any one of the three is enough: a session that administers only the research register reaches
  // the screen and sees that tab alone — see institutionTabs.
  {
    to: '/app/admin/institutii',
    label: 'adminNavInstitutions',
    icon: 'people',
    permission: INSTITUTION_PERMISSIONS,
  },
  {
    to: '/app/admin/utilizatori',
    label: 'adminNavUsers',
    icon: 'people',
    permission: PERMISSION.usersAdmin,
  },
  // Beside "Utilizatori" and on the same permission: every route behind it asks for users.admin.
  {
    to: '/app/admin/roluri',
    label: 'adminNavRoles',
    icon: 'lock',
    permission: PERMISSION.usersAdmin,
  },
  {
    to: '/app/admin/audit',
    label: 'adminNavAuditLog',
    icon: 'history',
    permission: PERMISSION.auditRead,
  },
  {
    to: '/app/admin',
    label: 'adminNavUsageStats',
    icon: 'chart',
    exact: true,
    permission: PERMISSION.reportsRead,
  },
  {
    to: '/app/admin/rapoarte/servicii-consiliere',
    label: 'adminNavAdvisoryReport',
    icon: 'services',
    permission: PERMISSION.reportsRead,
  },
  {
    to: '/app/admin/evaluari-evenimente',
    label: 'adminNavEventEvaluations',
    // Not chart again, which the entry above already shows: receipt-check — a returned form with
    // a tick — is otherwise unused in this nav and reads as the evaluations participants handed in.
    icon: 'receipt-check',
    permission: PERMISSION.reportsRead,
  },
  {
    to: '/app/admin/finantare',
    label: 'adminNavFunding',
    icon: 'coins',
    permission: PERMISSION.fundingManage,
  },
  {
    to: '/app/admin/agentii-sprijin',
    label: 'adminNavSupportAgencies',
    // No dedicated agency/globe icon in the sprite; shield is otherwise unused in this nav and
    // reads as "official body", which is the distinction from Funding (coins) this entry needs.
    icon: 'shield',
    permission: PERMISSION.fundingManage,
  },
  {
    to: '/app/admin/forumuri',
    label: 'adminNavForums',
    icon: 'bullet-list',
    permission: PERMISSION.forumsModerate,
  },
  {
    to: '/app/admin/workflows',
    label: 'adminNavWorkflows',
    icon: 'reorder',
    permission: PERMISSION.workflowAdmin,
  },
  {
    to: '/app/admin/configurare',
    label: 'adminNavConfig',
    icon: 'settings',
    permission: PERMISSION.nomenclatureAdmin,
  },
  {
    to: '/app/admin/backup',
    label: 'adminNavBackup',
    icon: 'download',
    permission: PERMISSION.backupManage,
  },
];

export function navFilter(items: NavItem[], roles: Set<string>, permissions: ReadonlySet<string>) {
  return items.filter((i) => {
    if (i.permission) return holdsPermission(permissions, i.permission);
    if (i.roles) return i.roles.some((r) => roles.has(r));
    return true;
  });
}
